import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  bindEmailContact,
  bindPhoneContact,
  getAccountSecurity,
  removeEmailContact,
  removePhoneContact,
  sendEmailContactCode,
  sendPhoneContactCode,
  type AccountSecurity,
} from '@/api/user'
import {
  isNationalNumberValid,
  normalizeNationalNumber,
  PHONE_COUNTRIES,
  toE164Phone,
} from '@/pages/login/phoneCountries'

type ContactType = 'phone' | 'email'

interface Props {
  onClose: () => void
  onDeleteAccount: () => void
}

export default function AccountSecuritySheet({ onClose, onDeleteAccount }: Props) {
  const { t } = useTranslation()
  const [data, setData] = useState<AccountSecurity | null>(null)
  const [editing, setEditing] = useState<ContactType | null>(null)
  const [countryCode, setCountryCode] = useState('CN')
  const [value, setValue] = useState('')
  const [otp, setOtp] = useState('')
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const country = useMemo(
    () => PHONE_COUNTRIES.find((item) => item.code === countryCode) ?? PHONE_COUNTRIES[0],
    [countryCode],
  )

  const load = useCallback(async () => {
    setBusy(true)
    setError('')
    try {
      setData(await getAccountSecurity())
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('settings_securityLoadFailed'))
    } finally {
      setBusy(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const resetEditor = () => {
    setEditing(null)
    setValue('')
    setOtp('')
    setChallengeId(null)
    setError('')
  }

  const normalizedValue = () => {
    if (editing === 'phone') return toE164Phone(value, country)
    return value.trim().toLowerCase()
  }

  const valueIsValid = editing === 'phone'
    ? isNationalNumberValid(value, country)
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

  const sendCode = async () => {
    if (!editing || !valueIsValid) return
    setBusy(true)
    setError('')
    try {
      const result = editing === 'phone'
        ? await sendPhoneContactCode(normalizedValue())
        : await sendEmailContactCode(normalizedValue())
      setChallengeId(result.challenge_id)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('settings_securityOperationFailed'))
    } finally {
      setBusy(false)
    }
  }

  const saveContact = async () => {
    if (!editing || !challengeId || otp.length < 4) return
    setBusy(true)
    setError('')
    try {
      const result = editing === 'phone'
        ? await bindPhoneContact(normalizedValue(), challengeId, otp)
        : await bindEmailContact(normalizedValue(), challengeId, otp)
      setData(result)
      resetEditor()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('settings_securityOperationFailed'))
    } finally {
      setBusy(false)
    }
  }

  const removeContact = async (type: ContactType) => {
    if (!window.confirm(t('settings_contactRemoveConfirm'))) return
    setBusy(true)
    setError('')
    try {
      if (type === 'phone') await removePhoneContact()
      else await removeEmailContact()
      await load()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('settings_securityOperationFailed'))
    } finally {
      setBusy(false)
    }
  }

  const providerLabels: Record<string, string> = {
    phone_otp: t('settings_providerPhone'),
    email_otp: t('settings_providerEmail'),
    google: 'Google',
    apple: 'Apple',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40">
      <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl max-h-[88vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-800">{t('settings_accountSecurity')}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
          {busy && !data ? <p className="text-sm text-gray-400 py-6 text-center">{t('settings_loading')}</p> : null}
          {error ? <p className="text-xs text-red-500 bg-red-50 rounded-xl p-3 mb-3">{error}</p> : null}

          {data ? (
            <>
              <p className="text-xs font-semibold text-gray-400 mb-2">{t('settings_loginAccount')}</p>
              <div className="rounded-2xl bg-purple-50 p-4 mb-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {providerLabels[data.login_account.provider] ?? data.login_account.provider}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{data.login_account.masked_identifier ?? '—'}</p>
                  </div>
                  <span className="text-xs text-purple-600 font-medium">{t('settings_primaryLogin')}</span>
                </div>
                <p className="text-xs text-gray-500 mt-3 leading-relaxed">{t('settings_loginImmutableHint')}</p>
              </div>

              <p className="text-xs font-semibold text-gray-400 mb-2">{t('settings_contactInfo')}</p>
              <p className="text-xs text-amber-600 bg-amber-50 rounded-xl p-3 mb-2">{t('settings_contactNotLoginHint')}</p>
              {(['phone', 'email'] as const).map((type) => {
                const contact = data.contacts.find((item) => item.type === type)
                return (
                  <div key={type} className="flex items-center justify-between py-3.5 border-b border-gray-50">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {type === 'phone' ? t('settings_phone') : t('settings_email')}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{contact?.masked_value ?? t('settings_notBound')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {contact ? (
                        <button disabled={busy} onClick={() => void removeContact(type)} className="text-xs text-red-400 disabled:opacity-40">
                          {t('settings_removeContact')}
                        </button>
                      ) : null}
                      <button
                        disabled={busy}
                        onClick={() => { resetEditor(); setEditing(type) }}
                        className="text-xs text-pink-500 font-medium disabled:opacity-40"
                      >
                        {contact ? t('settings_changeContact') : t('settings_bindContact')}
                      </button>
                    </div>
                  </div>
                )
              })}

              {editing ? (
                <div className="mt-4 rounded-2xl bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-800 mb-3">
                    {editing === 'phone' ? t('settings_bindPhone') : t('settings_bindEmail')}
                  </p>
                  <div className="flex gap-2">
                    {editing === 'phone' ? (
                      <select
                        value={countryCode}
                        onChange={(event) => { setCountryCode(event.target.value); setValue(''); setChallengeId(null) }}
                        className="h-11 rounded-xl bg-white px-2 text-sm outline-none"
                      >
                        {PHONE_COUNTRIES.map((item) => (
                          <option key={item.code} value={item.code}>{item.flag} {item.dialCode}</option>
                        ))}
                      </select>
                    ) : null}
                    <input
                      type={editing === 'email' ? 'email' : 'tel'}
                      value={value}
                      onChange={(event) => {
                        setValue(editing === 'phone' ? normalizeNationalNumber(event.target.value, country) : event.target.value)
                        setChallengeId(null)
                      }}
                      placeholder={editing === 'phone' ? t('settings_phonePlaceholder') : t('settings_emailPlaceholder')}
                      className="flex-1 min-w-0 h-11 rounded-xl bg-white px-3 text-sm outline-none"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <input
                      value={otp}
                      onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 8))}
                      placeholder={t('settings_codePlaceholder')}
                      className="flex-1 min-w-0 h-11 rounded-xl bg-white px-3 text-sm outline-none"
                    />
                    <button
                      disabled={!valueIsValid || busy}
                      onClick={() => void sendCode()}
                      className="px-3 h-11 rounded-xl bg-white text-pink-500 text-xs font-medium disabled:opacity-40"
                    >
                      {challengeId ? t('settings_resendCode') : t('settings_sendCode')}
                    </button>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={resetEditor} className="flex-1 h-10 rounded-xl bg-white text-gray-500 text-sm">{t('settings_cancel')}</button>
                    <button
                      disabled={!challengeId || otp.length < 4 || busy}
                      onClick={() => void saveContact()}
                      className="flex-1 h-10 rounded-xl bg-pink-500 text-white text-sm disabled:opacity-40"
                    >
                      {t('settings_confirmBind')}
                    </button>
                  </div>
                </div>
              ) : null}

              <button onClick={onDeleteAccount} className="w-full flex items-center justify-between py-4 mt-3">
                <p className="text-sm font-medium text-red-500">{t('settings_deleteAccount')}</p>
                <i className="ri-arrow-right-s-line text-gray-300 text-base" />
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
